import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { TrendingDown as TrendingDownIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TopSectorsCards = ({ topSectors, bottomSectors }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Helper function to get sector name based on current language
  const getSectorName = (sector) => {
    if (currentLang === 'vi' && sector.sector_name_vi) {
      return sector.sector_name_vi;
    }
    return sector.sector_name_en || sector.sector_name || sector.valuation_sector;
  };
  
  const getRecommendationLabel = (recommendation) => {
    switch (recommendation) {
      case 'Strong Buy':
        return t('rankings.strongBuy');
      case 'Buy':
        return t('rankings.buy');
      case 'Hold':
        return t('rankings.hold');
      case 'Underweight':
        return t('rankings.underweight');
      case 'Avoid':
        return t('rankings.avoid');
      case 'Sell':
        return t('rankings.sell');
      default:
        return recommendation;
    }
  };
  
  const getRecommendationColor = (rec) => {
    if (rec === 'Strong Buy' || rec === 'Buy') return '#11998e';
    if (rec === 'Hold') return '#ffa726';
    return '#ff6b6b';
  };

  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(2, 1fr)', 
      gap: 2, 
      mb: 3,
      width: '100%',
    }}>
      {/* Top Sectors to BUY */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                boxShadow: '0 4px 16px 0 rgba(102,126,234,0.10)',
                mr: 2,
              }}
            >
              <AutoAwesomeIcon fontSize="large" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('dashboard.topSectors.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.topSectors.subtitle')}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {topSectors.length > 0 ? topSectors.map((sector, index) => (
              <Box 
                key={sector.sector_name}
                sx={{ 
                  p: 2,
                  bgcolor: alpha('#11998e', 0.05),
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#11998e', 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: '#11998e',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {getSectorName(sector)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.topSectors.tailwind')}: {parseFloat(sector.tailwind_score).toFixed(1)} | {t('dashboard.topSectors.val')}: {parseFloat(sector.val_score).toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#11998e' }}>
                    {parseFloat(sector.final_score).toFixed(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    px: 1, 
                    py: 0.5, 
                    borderRadius: 1, 
                    bgcolor: alpha('#11998e', 0.1),
                    color: '#11998e',
                    fontWeight: 600,
                  }}>
                    {getRecommendationLabel(sector.recommendation)}
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('common.noData')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Bottom Sectors - Lower Ranked */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ffa726', 0.1),
                color: '#ffa726',
                mr: 2,
              }}
            >
              <TrendingDownIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('dashboard.bottomSectors.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.bottomSectors.subtitle')}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {bottomSectors.length > 0 ? bottomSectors.map((sector) => {
              const color = getRecommendationColor(sector.recommendation);
              
              return (
                <Box 
                  key={sector.sector_name}
                  sx={{ 
                    p: 2,
                    bgcolor: alpha(color, 0.05),
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: alpha(color, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#ffa726',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    >
                      #{sector.ranking}
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {getSectorName(sector)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('dashboard.topSectors.tailwind')}: {parseFloat(sector.tailwind_score).toFixed(1)} | {t('dashboard.topSectors.val')}: {parseFloat(sector.val_score).toFixed(1)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: color }}>
                      {parseFloat(sector.final_score).toFixed(1)}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1, 
                      bgcolor: alpha(color, 0.1),
                      color: color,
                      fontWeight: 600,
                    }}>
                      {getRecommendationLabel(sector.recommendation)}
                    </Typography>
                  </Box>
                </Box>
              );
            }) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                {t('common.noData')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TopSectorsCards;

